import React from "react";
import RichTextNode from "./RichTextNode";
import VideoNode from "./VideoNode";
import FormNode from "./FormNode";
import CanvasNode from "./CanvasNode";
import AttachmentNode from "./AttachmentNode";

/**
 * ChildrenRenderer Component
 *
 * Renders sorted children nodes (Rich Text, Video, Form, Canvas, Attachment)
 * Used by both StepNode and SubstepNode to avoid code duplication
 *
 * @param {Array} children - Array of child nodes
 * @param {Object} parentContext - Parent hierarchy context (sectionIndex, stepIndex, etc.)
 * @param {Function} onNavigate - Navigation callback
 * @param {Object} checkboxState - Checkbox state control
 * @param {boolean} disabled - If true, all child checkboxes will be disabled
 * @param {boolean} showCheckbox - If false, all child checkboxes will be hidden
 * @param {Array} overrideNodeNumbers - Optional array of node numbers to override calculation
 * @param {string[]|null} hideChildHeadingsForTypes - Child `type` values here keep body data but hide the heading row (checkbox/title/breadcrumb), e.g. Document live (view) mode
 */
const ChildrenRenderer = ({
  children = [],
  parentContext = {},
  onNavigate,
  checkboxState,
  disabled = false,
  showCheckbox = true,
  showHeading = null,
  allNodesData = [],
  checkboxLabelColor = null,
  isLock = false,
  isRevisionLocked = false,
  overrideNodeNumbers = null,
  hideChildHeadingsForTypes = null,
  enableCanvasStagePopup = false,
}) => {
  /** API / snapshots use "canva"; {@link processContentData} normalizes to "canvas" for audit preview — count & render both. */
  const isCanvasChildType = (t) => t === "canva" || t === "canvas";

  // Sort children by sibling_order
  const sortedChildren = [...children].sort((a, b) => {
    const orderA = parseInt(a.sibling_order || 0, 10);
    const orderB = parseInt(b.sibling_order || 0, 10);
    return orderA - orderB;
  });

  const headingForChild = (childType) =>
    hideChildHeadingsForTypes?.includes(childType) ? false : showHeading;

  // Calculate global node number by type across all sections, steps, and substeps
  const calculateGlobalNodeNumberByType = (currentChild) => {
    const nodeType = currentChild.type;
    const typeMatches = (c) =>
      c &&
      (c.type === nodeType ||
        (isCanvasChildType(c.type) && isCanvasChildType(nodeType)));
    let counter = 0;

    // Sort all sections by sibling_order (same logic as in ViewChildNode)
    const compareSiblingOrders = (a, b) => {
      const intA = parseInt(a, 10);
      const intB = parseInt(b, 10);
      return intA - intB;
    };

    const sortedSections = [...allNodesData].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
      const orderCompare = compareSiblingOrders(orderA, orderB);
      if (orderCompare !== 0) return orderCompare;
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    // Count all nodes of this type before this one across all sections, steps, and substeps
    for (let i = 0; i < sortedSections.length; i++) {
      const section = sortedSections[i];

      for (let j = 0; j < (section.steps?.length || 0); j++) {
        const step = section.steps[j];

        // Count step-level children
        const stepChildren =
          step.children?.filter((c) => typeMatches(c)) || [];

        let stepCounterResult = null;
        for (let idx = 0; idx < stepChildren.length; idx++) {
          if (stepChildren[idx] === currentChild) {
            stepCounterResult = counter + idx + 1;
            break;
          }
        }

        if (stepCounterResult !== null) {
          return stepCounterResult;
        }

        counter += stepChildren.length;

        // Count substep-level children
        for (let k = 0; k < (step.substeps?.length || 0); k++) {
          const substep = step.substeps[k];
          const substepChildren =
            substep.children?.filter((c) => typeMatches(c)) || [];

          let substepCounterResult = null;
          for (let idx = 0; idx < substepChildren.length; idx++) {
            if (substepChildren[idx] === currentChild) {
              substepCounterResult = counter + idx + 1;
              break;
            }
          }

          if (substepCounterResult !== null) {
            return substepCounterResult;
          }

          counter += substepChildren.length;
        }
      }
    }
    return counter + 1;
  };

  return (
    <>
      {sortedChildren.map((child, childIndex) => {
        // Use override node number if provided, otherwise calculate it
        const nodeNumber =
          overrideNodeNumbers && overrideNodeNumbers[childIndex]
            ? overrideNodeNumbers[childIndex]
            : calculateGlobalNodeNumberByType(child);

        switch (child.type) {
          case "rich-text":
            return (
              <RichTextNode
                key={child.tree_node_id}
                node={child}
                nodeNumber={nodeNumber}
                parentContext={parentContext}
                nodeType="rich-text"
                onNavigate={onNavigate}
                checkboxState={checkboxState}
                disabled={disabled}
                showCheckbox={showCheckbox}
                showHeading={headingForChild("rich-text")}
                checkboxLabelColor={checkboxLabelColor}
                isLock={isLock}
                isRevisionLocked={isRevisionLocked}
              />
            );
          case "video":
            return (
              <VideoNode
                key={child.tree_node_id}
                node={child}
                nodeNumber={nodeNumber}
                parentContext={parentContext}
                nodeType="video"
                onNavigate={onNavigate}
                checkboxState={checkboxState}
                disabled={disabled}
                showCheckbox={showCheckbox}
                showHeading={headingForChild("video")}
                checkboxLabelColor={checkboxLabelColor}
                isRevisionLocked={isRevisionLocked}
              />
            );
          case "form":
            return (
              <FormNode
                key={child.tree_node_id}
                node={child}
                nodeNumber={nodeNumber}
                parentContext={parentContext}
                nodeType="form"
                onNavigate={onNavigate}
                checkboxState={checkboxState}
                disabled={false}
                showCheckbox={showCheckbox}
                showHeading={headingForChild("form")}
                checkboxLabelColor={checkboxLabelColor}
                isRevisionLocked={isRevisionLocked}
              />
            );
          case "canvas":
          case "canva":
            return (
              <CanvasNode
                key={child.detail?.canvas_tree_node_id || child.tree_node_id}
                node={child}
                nodeNumber={nodeNumber}
                parentContext={parentContext}
                nodeType="canva"
                onNavigate={onNavigate}
                checkboxState={checkboxState}
                disabled={disabled}
                showCheckbox={showCheckbox}
                showHeading={headingForChild("canva")}
                checkboxLabelColor={checkboxLabelColor}
                isLocked={isLock}
                isRevisionLocked={isRevisionLocked}
                enableStagePopup={enableCanvasStagePopup}
              />
            );
          case "attachment":
            return (
              <AttachmentNode
                key={child.tree_node_id}
                node={child}
                nodeNumber={nodeNumber}
                parentContext={parentContext}
                nodeType="attachment"
                onNavigate={onNavigate}
                checkboxState={checkboxState}
                disabled={disabled}
                showCheckbox={showCheckbox}
                showHeading={headingForChild("attachment")}
                checkboxLabelColor={checkboxLabelColor}
                isRevisionLocked={isRevisionLocked}
              />
            );
          default:
            console.warn("Unknown child node type:", child.type);
            return null;
        }
      })}
    </>
  );
};

/** Heading row hidden (data kept) on Document training checkmark screen (after Select Trainer → Next). */
export const HIDE_CHILD_HEADING_TYPES_TRAINING_CHECKMARK = [
  "rich-text",
  "video",
  "canva",
  "canvas",
  "attachment",
];

export default ChildrenRenderer;
